import React, { useContext, useEffect, useState } from "react"
import AppContext from "../../shared/AppContext"
import { Redirect } from "react-router"
import SearchBar from "../../components/SearchBar"
import gql from "graphql-tag"
import { Query } from "react-apollo"
import useDebounce from "../../hooks/useDebounce"
import { Waypoint } from "react-waypoint"
import UpsellModal from "./components/UpsellModal"

import "./observatorybundles.scss"
import Card from "./components/Card"

const SEARCH_OBSERVATORY_BUNDLES = gql`
  query SearchObservatoryBundles(
    $searchKeyword: String
    $offset: Int!
    $hasAccessOnly: Boolean!
  ) {
    searchObservatoryBundles(
      searchKeyword: $searchKeyword
      offset: $offset
      hasAccessOnly: $hasAccessOnly
    ) {
      id
      name
      description
      hasAccess
      marketArrayReport {
        id
        name
        slug
      }
      observatoryReport {
        id
        name
        slug
      }
      insightsReport {
        id
        name
        slug
      }
      drilldown {
        id
        name
        slug
      }
    }
  }
`

const ObservatoryBundles = (props) => {
  const { isUserAuthenticated, roles } = props.viewer
  const { trackEvent } = useContext(AppContext)
  const [hasAccessOnly, setHasAccessOnly] = useState(false)

  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false)
  const [selectedBundleTitle, setSelectedBundleTitle] = useState(false)

  const {
    value: searchKeyword,
    deferredValue: deferredKeyword,
    setValue: setSearchKeyword,
  } = useDebounce(250)

  const trackSearchKeyword = () => {
    if (!deferredKeyword) return

    trackEvent("Observatory Bundle Search", {
      keywordSearch: deferredKeyword,
    })
  }

  useEffect(trackSearchKeyword, [deferredKeyword])

  if (!isUserAuthenticated) {
    return (
      <Redirect
        to={{ pathname: "/login", state: { from: "/observatory-bundles" } }}
      />
    )
  }

  const hasFullAccessRoles = [
    "ObservatoryBundles",
    "ETR Master Admin",
    "ETR Research Admin",
    "ETR Sales Admin",
  ]

  const hasMarketArrayRole = hasFullAccessRoles.some((role) =>
    roles.includes(role)
  )

  const onLoadMoreObservatoryBundles = (data, fetchMore) => {
    const currentLength = data.searchObservatoryBundles.length

    fetchMore({
      variables: {
        offset: currentLength,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev
        return Object.assign({}, prev, {
          searchObservatoryBundles: [
            ...prev.searchObservatoryBundles,
            ...fetchMoreResult.searchObservatoryBundles,
          ],
        })
      },
    })
  }

  const globalWindow = typeof window !== "undefined" ? window : undefined

  return (
    <>
      <div className="observatory-bundles-container">
        <div className="observatory-bundle-filters">
          <SearchBar
            classes={["border", "rounded"]}
            placeholder={"Search observatory bundles"}
            value={searchKeyword}
            onChange={(e) => {
              setSearchKeyword(e.target.value)
            }}
          />

          {!hasMarketArrayRole && (
            <div className="my-bundle-only">
              <label htmlFor="show-user-owned">
                Show accessible bundles only
              </label>
              <input
                type="checkbox"
                id="show-user-owned"
                name="show-user-owned"
                onChange={() => {
                  setHasAccessOnly(!hasAccessOnly)
                }}
              />
            </div>
          )}
        </div>

        <div className="observatory-bundles-list-container">
          <Query
            query={SEARCH_OBSERVATORY_BUNDLES}
            fetchPolicy="network-only"
            variables={{
              searchKeyword: deferredKeyword,
              offset: 0,
              hasAccessOnly: hasAccessOnly,
            }}
          >
            {({ data, error, loading, fetchMore }) => {
              if (loading) {
                return <div>Loading...</div>
              }

              if (error || !data || !data.searchObservatoryBundles) {
                return <div>An error occurred while getting bundles.</div>
              }

              if (!data.searchObservatoryBundles.length) {
                return <div>No observatory bundles found.</div>
              }

              if (data && data.searchObservatoryBundles) {
                return data.searchObservatoryBundles.map((bundle, i) => {
                  return (
                    <div key={i} style={{ display: "flex" }}>
                      <Card
                        bundle={bundle}
                        setIsUpsellModalOpen={(val) =>
                          setIsUpsellModalOpen(val)
                        }
                        setSelectedBundleTitle={(title) =>
                          setSelectedBundleTitle(title)
                        }
                      />

                      {i === data.searchObservatoryBundles.length - 5 && (
                        <Waypoint
                          scrollableAncestor={globalWindow}
                          onEnter={() =>
                            onLoadMoreObservatoryBundles(data, fetchMore)
                          }
                        />
                      )}
                    </div>
                  )
                })
              }

              return null
            }}
          </Query>
        </div>
      </div>
      <UpsellModal
        isOpen={isUpsellModalOpen}
        title={selectedBundleTitle}
        setIsUpsellModalOpen={setIsUpsellModalOpen}
      />
    </>
  )
}

export default ObservatoryBundles
